import { CalendarIcon, Info, Link2Icon } from 'lucide-react'
import moment from 'moment'
import { useState } from 'react'
import { FieldPath, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { Loading } from '@/components/Loading'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Checkbox } from '@/components/ui/checkbox'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { apiFetcher } from '@/data/fetcher'
import { PaymentMethod } from '@/enums/PaymentMethods'
import { Collection } from '@/interfaces/Collection'
import { Settings } from '@/interfaces/Settings'
import { formatDate } from '@/lib/formats/formatDate'
import { cn } from '@/lib/utils'
import { components } from '@invo.space/shared'
import { Currency } from '@/types/Currency'

import { DocumentFormValues } from '../DocumentForm'

type InvoicingBankAccount = components['schemas']['InvoicingBankAccount']

export interface InvoiceSettingsFormProps {
  type: 'expense' | 'invoice' | 'order'
  settings?: Settings
}

/** Data ve formuláři jsou ve tvaru "1. 11. 2026" (formatDate, cs-CZ). */
const parseDate = (value?: string) => {
  if (!value) return undefined
  const m = moment(value, 'D. M. YYYY')
  return m.isValid() ? m : undefined
}

const DUE_PRESETS = [0, 7, 10, 14, 15, 30]

const dueLabel = (days: number) => (days === 0 ? 'Ihned' : `${days} dní`)

const Field = ({
  label,
  title,
  hint,
  action,
  className,
  children,
}: {
  label: string
  title?: string
  hint?: string
  action?: React.ReactNode
  className?: string
  children: React.ReactNode
}) => (
  <div className={cn('grid min-w-0 content-start gap-1.5 [&_button[role=combobox]]:w-full [&_button[role=combobox]]:min-w-0 [&_[data-slot=select-value]]:truncate', className)}>
    <div className="flex min-h-5 items-center justify-between gap-2">
      <div className="flex items-center gap-1.5">
        {title ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Label className="m-0 text-[13px] text-muted-foreground">{label}</Label>
            </TooltipTrigger>
            <TooltipContent>{title}</TooltipContent>
          </Tooltip>
        ) : (
          <Label className="m-0 text-[13px] text-muted-foreground">{label}</Label>
        )}
        {hint && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="size-3.5 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>{hint}</TooltipContent>
          </Tooltip>
        )}
      </div>
      {action}
    </div>
    {children}
  </div>
)

const DatePickerButton = ({
  value,
  onChange,
  placeholder,
  className,
  ariaLabel,
}: {
  value?: string
  onChange: (date: Date) => void
  placeholder: string
  className?: string
  ariaLabel?: string
}) => {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label={ariaLabel}
          className={cn(
            'w-full justify-start text-left font-normal tabular',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="text-muted-foreground" />
          {value || placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={parseDate(value)?.toDate() ?? new Date()}
          defaultMonth={parseDate(value)?.toDate()}
          onSelect={(date) => {
            if (!date) return
            onChange(date)
            setOpen(false)
          }}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  )
}

export const InvoiceSettingsForm = ({
  type,
  settings,
}: InvoiceSettingsFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'documentForm.settings',
  })
  const { t: tg } = useTranslation(undefined, {
    keyPrefix: 'generic',
  })
  const { t: tp } = useTranslation(undefined, {
    keyPrefix: 'paymentMethod',
  })
  const methods = useFormContext<DocumentFormValues>()
  const { control, register, watch, setValue, getValues } = methods
  const issueDate = watch('settings.issueDate')
  const taxDate = watch('settings.taxDate')
  const receivedDate = watch('settings.receivedDate')
  const dueDate = watch('settings.dueDate')
  const vatDeductible = watch('settings.vatDeductible')

  const showTaxDate = type !== 'order' && settings?.vatPayer === 'vatPayer'
  // DUZP je obvykle stejné jako datum vystavení: dokud ho uživatel nezmění, drží se data vystavení
  const [taxDateCustom, setTaxDateCustom] = useState(
    () => !!getValues('settings.taxDate') && getValues('settings.taxDate') !== getValues('settings.issueDate'),
  )

  const issueMoment = parseDate(issueDate)
  const dueMoment = parseDate(dueDate)
  const dueOffset =
    issueMoment && dueMoment ? dueMoment.startOf('day').diff(issueMoment.clone().startOf('day'), 'days') : null
  const activePreset = dueOffset !== null && DUE_PRESETS.includes(dueOffset) ? dueOffset : null

  const pamentMethodMap: Record<PaymentMethod, string> = {
    bankTransfer: tp('bankTransfer'),
    cash: tp('cash'),
    card: tp('card'),
    cod: tp('cod'),
    other: tp('other'),
  }

  const handleChangeDate = (
    name: FieldPath<DocumentFormValues>,
    date: Date,
  ) => {
    const newDate = date ? formatDate(date) : date

    setValue(name, newDate)
  }

  const handleChangeIssueDate = (date: Date) => {
    handleChangeDate('settings.issueDate', date)

    if (showTaxDate && !taxDateCustom) {
      handleChangeDate('settings.taxDate', date)
    }

    // zvolená splatnost (např. 14 dní) se posune s datem vystavení
    if (activePreset !== null) {
      handleChangeDate('settings.dueDate', moment(date).add(activePreset, 'days').toDate())
    }
  }

  const handleDuePreset = (days: number) => {
    const base = issueMoment ? issueMoment.clone() : moment()
    handleChangeDate('settings.dueDate', base.add(days, 'days').toDate())
  }

  const { data: currencies, isLoading } = useSWR<Collection<Currency>>(
    '/api/v2/currencies',
    apiFetcher,
  )

  const { data: bankAccountsData } = useSWR<{ data?: InvoicingBankAccount[] }>(
    type !== 'expense' ? '/api/v2/bank-accounts' : null,
    apiFetcher,
  )
  const bankAccounts = bankAccountsData?.data
  const showBankAccount = type !== 'expense' && bankAccounts && bankAccounts.length > 0
  const showCurrency = !!settings?.alternativeCurrencies && settings.alternativeCurrencies.length > 0

  if (isLoading) {
    return <Loading />
  }

  return (
    <div className="grid w-full grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
      <Field label="Datum vystavení">
        <DatePickerButton
          value={issueDate}
          placeholder={tg('pickDate')}
          onChange={handleChangeIssueDate}
        />
      </Field>

      {showTaxDate && (
        <Field
          label="DUZP"
          title="Datum uskutečnění zdanitelného plnění"
          action={
            taxDateCustom ? (
              <Button
                type="button"
                variant="link"
                className="h-5 text-[13px]"
                onClick={() => {
                  setValue('settings.taxDate', issueDate)
                  setTaxDateCustom(false)
                }}
              >
                Stejné jako vystavení
              </Button>
            ) : null
          }
        >
          {taxDateCustom ? (
            <DatePickerButton
              value={taxDate}
              placeholder={tg('pickDate')}
              onChange={(date) => handleChangeDate('settings.taxDate', date)}
            />
          ) : (
            <div className="flex h-8 items-center justify-between gap-2 rounded-md bg-muted pl-3 pr-1 text-sm">
              <span className="flex min-w-0 items-center gap-2 truncate">
                <Link2Icon className="size-4 shrink-0 text-muted-foreground" />
                <span className="tabular">{taxDate || issueDate}</span>
                <span className="truncate text-muted-foreground">stejné jako vystavení</span>
              </span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setTaxDateCustom(true)}>
                Změnit
              </Button>
            </div>
          )}
        </Field>
      )}

      {type === 'expense' && (
        <Field
          label="Datum přijetí faktury"
          hint="Datum, kdy jste fakturu přijali, určuje období, kdy můžete nárokovat odpočet DPH"
        >
          <DatePickerButton
            value={receivedDate}
            placeholder={tg('pickDate')}
            onChange={(date) => handleChangeDate('settings.receivedDate', date)}
          />
        </Field>
      )}

      <Field label="Splatnost" className="sm:col-span-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div role="radiogroup" aria-label="Splatnost ve dnech" className="inline-flex flex-wrap items-center gap-1">
            {DUE_PRESETS.map((days) => {
              const active = activePreset === days
              return (
                <button
                  key={days}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => handleDuePreset(days)}
                  className={cn(
                    'inline-flex h-8 items-center rounded-full border px-3 text-[13px] font-medium tabular transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                    active
                      ? 'border-transparent bg-foreground text-background'
                      : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  {dueLabel(days)}
                </button>
              )
            })}
          </div>
          <div className="w-full sm:ml-auto sm:w-44">
            <DatePickerButton
              value={dueDate}
              ariaLabel="Datum splatnosti"
              placeholder={tg('pickDate')}
              onChange={(date) => handleChangeDate('settings.dueDate', date)}
            />
          </div>
        </div>
      </Field>

      <Field label="Způsob platby">
        <Select
          value={watch('settings.paymentMethod')}
          onValueChange={(value) => {
            setValue('settings.paymentMethod', value as PaymentMethod)
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Vyberte způsob platby" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(pamentMethodMap).map(([key, value]) => (
              <SelectItem key={key} value={key}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      {showBankAccount && (
        <Field label={t('fields.bankAccount.label')}>
          <Select
            value={watch('settings.bankAccountId') ?? ''}
            onValueChange={(value) => {
              setValue('settings.bankAccountId', value || null)
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('fields.bankAccount.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {bankAccounts!.map((account) => (
                <SelectItem key={account.id} value={account.id!}>
                  {account.name}
                  {account.accountNumber
                    ? ` (${account.accountNumber}/${account.bankCode ?? ''})`
                    : account.iban
                      ? ` (${account.iban})`
                      : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}

      <Field label={t('fields.vs.label')}>
        <Input
          className="tabular"
          placeholder={t('fields.vs.placeholder')}
          {...register('settings.variableSymbol')}
        />
      </Field>

      {showCurrency && (
        <Field label="Měna">
          <Select
            value={watch('settings.currency')}
            onValueChange={(value) => {
              setValue('settings.currency', value)
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Vyberte měnu" />
            </SelectTrigger>
            <SelectContent>
              {[
                settings.primaryCurrency!,
                ...settings.alternativeCurrencies!,
              ].map((currency) => (
                <SelectItem key={currency} value={currency}>
                  {
                    currencies?.data.find((c) => c.code === currency)
                      ?.symbol
                  }
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}

      {type === 'expense' && (
        <Field label={t('fields.externalId.label')}>
          <Input {...register('settings.externalId')} />
        </Field>
      )}

      {type === 'expense' && (
        <FormField
          control={control}
          name="settings.expenseType"
          render={({ field }) => (
            <FormItem className="grid gap-1.5 sm:col-span-2">
              <FormLabel className="text-[13px] text-muted-foreground">{t('fields.expenseType.label')}</FormLabel>
              <FormControl>
                <RadioGroup
                  className="flex flex-row flex-wrap items-center gap-4"
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  {(['invoice', 'receipt', 'other'] as const).map((value) => (
                    <FormItem className="flex items-center gap-2 space-y-0" key={value}>
                      <FormControl>
                        <RadioGroupItem
                          value={value}
                          id={value}
                          onBlur={field.onBlur}
                        />
                      </FormControl>
                      <FormLabel htmlFor={value} className="font-normal">
                        {t(`fields.expenseType.options.${value}`)}
                      </FormLabel>
                    </FormItem>
                  ))}
                </RadioGroup>
              </FormControl>
            </FormItem>
          )}
        />
      )}

      {type === 'expense' && (
        <FormField
          control={control}
          name="settings.vatDeductible"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center gap-2 space-y-0 sm:col-span-2">
              <FormControl>
                <Checkbox
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="font-normal">{t('fields.vatDeductible.label')}</FormLabel>
            </FormItem>
          )}
        />
      )}

      {type === 'expense' && vatDeductible && (
        <FormField
          control={control}
          name="settings.vatDeductionProportionPercent"
          render={({ field }) => (
            <FormItem className="grid gap-1.5">
              <FormLabel className="text-[13px] text-muted-foreground">
                {t('fields.vatDeductionProportionPercent.label')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onChange={(e) => {
                    const value = e.target.value
                    const number = Number(value)
                    if (number > 100) {
                      field.onChange(100)
                    } else if (number < 0) {
                      field.onChange(0)
                    } else {
                      field.onChange(number)
                    }
                  }}
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  )
}
